import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useMutation } from "@tanstack/react-query";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { AnimatePresence, motion } from "motion/react";
import { useContext, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Button, EyeIcon } from "../../../atoms";
import { XIcon } from "../../../atoms/icons/XIcon";
import { AppSettingsContext } from "../components/AppSettingsContext";
import { AppSettingsOption } from "../components/AppSettingsOption";
import { AppSettingsNavigationPreview } from "./AppSettingsNavigationPreview";

export const AppSettingsNavigation = () => {
  const { t } = useTranslation();
  const { mutateAsync: updateAppNavigation } = useMutation(
    apiV2.staff.appSettings.mutations.updateNavigationSettings(),
  );
  const [parent] = useAutoAnimate();
  const { navigationSettings, setNavigationSettings } =
    useContext(AppSettingsContext);
  const [showPreview, setShowPreview] = useState(false);
  return (
    <>
      {createPortal(
        <AnimatePresence>
          {showPreview && (
            <motion.div
              className="absolute inset-0 z-50 flex flex-col items-center justify-start bg-gray-100 pt-4 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="z-10 flex w-full flex-row items-center justify-between gap-4 p-4 pb-0">
                <div className="text-xl font-semibold text-gray-950">
                  {t("pages.settings.appSettings.navigation.preview")}
                </div>
                <Button
                  intent="transparent"
                  onClick={() => {
                    setShowPreview(false);
                  }}
                >
                  <XIcon className="stroke-gray-950" />
                </Button>
              </div>
              <AppSettingsNavigationPreview className="pt-0" />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
      <div className="flex size-full flex-col justify-between gap-6">
        <div className="flex flex-col gap-6 pt-2" ref={parent}>
          <div className="flex flex-col gap-1">
            <div className="text-lg font-semibold text-gray-950">
              {t("page.settings.appSettings.navigation.title")}
            </div>
            <div className="text-sm font-medium text-gray-500">
              {t("page.settings.appSettings.navigation.description")}
            </div>
          </div>
          {navigationSettings && (
            <div className="flex flex-col gap-2">
              <AppSettingsOption.Container>
                <AppSettingsOption.Switch
                  value={true}
                  onChange={() => {}}
                  disabled
                />
                <AppSettingsOption.Label>
                  {t("page.settings.appSettings.navigation.homePage")}
                </AppSettingsOption.Label>
              </AppSettingsOption.Container>
              <AppSettingsOption.Container>
                <AppSettingsOption.Switch
                  value={navigationSettings.classesNavigationEnabled}
                  onChange={() => {
                    setNavigationSettings({
                      ...navigationSettings,
                      classesNavigationEnabled:
                        !navigationSettings.classesNavigationEnabled,
                    });
                  }}
                />
                <AppSettingsOption.Label>
                  {t("page.settings.appSettings.navigation.classes")}
                </AppSettingsOption.Label>
              </AppSettingsOption.Container>
              <AppSettingsOption.Container>
                <AppSettingsOption.Switch
                  value={navigationSettings.appointmentsNavigationEnabled}
                  onChange={() => {
                    setNavigationSettings({
                      ...navigationSettings,
                      appointmentsNavigationEnabled:
                        !navigationSettings.appointmentsNavigationEnabled,
                    });
                  }}
                />
                <AppSettingsOption.Label>
                  {t("page.settings.appSettings.navigation.appointments")}
                </AppSettingsOption.Label>
              </AppSettingsOption.Container>
              <AppSettingsOption.Container>
                <AppSettingsOption.Switch
                  value={navigationSettings.shopNavigationEnabled}
                  onChange={() => {
                    setNavigationSettings({
                      ...navigationSettings,
                      shopNavigationEnabled:
                        !navigationSettings.shopNavigationEnabled,
                    });
                  }}
                />
                <AppSettingsOption.Label>
                  {t("page.settings.appSettings.navigation.shop")}
                </AppSettingsOption.Label>
              </AppSettingsOption.Container>
              <AppSettingsOption.Container>
                <AppSettingsOption.Switch
                  value={true}
                  onChange={() => {}}
                  disabled
                />
                <AppSettingsOption.Label>
                  {t("page.settings.appSettings.navigation.profile")}
                </AppSettingsOption.Label>
              </AppSettingsOption.Container>
            </div>
          )}
        </div>
        <div className="-mx-6 -mb-6 flex flex-row justify-end gap-2 border-t border-gray-300 px-6 py-4">
          <Button
            className="w-1/2 gap-1 lg:hidden"
            onClick={async () => {
              setShowPreview(true);
            }}
          >
            <EyeIcon className="stroke-gray-950" />
            {t("pages.settings.appSettings.navigation.preview")}
          </Button>
          <Button
            intent="secondary"
            className="max-lg:w-1/2 lg:px-12"
            onClick={async () => {
              if (navigationSettings) {
                await updateAppNavigation(navigationSettings);
              }
            }}
          >
            {t("portal.common.save")}
          </Button>
        </div>
      </div>
    </>
  );
};
